import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { enUS } from 'date-fns/locale';
import { COMPONENTS, MetricNames, OVERALL } from '../utils/constants';
import { PrimaryButton } from '@fluentui/react';
import ProfilerIcon from '../Profiler.ico';
import { getComponentGraphData, getDatasetFromComponentTimeSeries } from './ComponentView.tsx';
import { ProfilePane } from './FlameGraph.tsx';
import { extractTimeSeriesData } from '../utils/flamegraphLauncher.ts';

// ===== Types =====

type ComponentDetailsGraphProps = {
    serviceId: number;
    env: number;
    metricType: number;
    process: number;
    type: number;
    commitDetails: any;
    profileDate?: string | null;
    nav?: ((updates: Record<string, any>) => void) | null;
};

type DeselectAllButtonProps = {
    chart: any;
};

// ===== Main Component =====

export const ComponentDetailsGraph: React.FC<ComponentDetailsGraphProps> = ({ serviceId, env, metricType, process, type, commitDetails, profileDate, nav }) => {
    const [chartInstance, setChartInstance] = useState<any>(null);
    const [localProfileDate, setLocalProfileDate] = useState<string | null>(null);

    const datasets = getDatasets(commitDetails, env, metricType, process, type);
    const handleChartInstance = (instance: any) => {
        setChartInstance(instance);
    };

    const setProfileDate = nav != null ? (date: string | null) => { nav({ profileDate: date }); } : setLocalProfileDate;

    const data = {
        datasets: datasets,
    };

    // Extract time series data for comparison mode
    const timeSeriesData = useMemo(() => {
        if (!chartInstance) return undefined;
        return extractTimeSeriesData(chartInstance);
    }, [chartInstance, datasets]);

    const options = getBasicChartOptions(metricType, setProfileDate);
    deleteTooltip();
    const showToggle = datasets.length > 1;
    return (
        <div>
            {showToggle && chartInstance && <DeselectAllButton chart={chartInstance} />}
            <div id="tooltipparent"></div>
            <Line data={data} options={options} ref={handleChartInstance} />
            {(localProfileDate ?? profileDate) && <ProfilePane mode="commit" serviceId={serviceId} commitId={commitDetails.commitId} env={env} process={process} metric={metricType} type={type} date={localProfileDate ?? profileDate} isCommitData={false} timeSeriesData={timeSeriesData} enableComparison={true} onclose={() => setProfileDate(null)} />}
        </div>
    );
};

// ===== Deselect All Button =====

const DeselectAllButton: React.FC<DeselectAllButtonProps> = ({ chart }) => {
    const [deselectAllText, setDeselectAllText] = useState('Deselect All');
    const handleDeselectAllClick = () => {
        const datasets = chart.data.datasets;
        const visibility = chart.getDatasetMeta(0).hidden;
        for (let i = 0; i < datasets.length; i++) {
            chart.getDatasetMeta(i).hidden = !visibility;
        }
        chart.update();
        if (visibility) {
            setDeselectAllText('Deselect All');
        } else {
            setDeselectAllText('Select All');
        }
    };
    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PrimaryButton onClick={handleDeselectAllClick}>{deselectAllText}</PrimaryButton>
        </div>
    );
};

// ===== Dataset Generation =====

export function getDatasets(commitDetails: any, env: number, metric: number, process: number, type: number) {
    const datasets: any[] = [];
    if (type === OVERALL) {
        const details = getComponentGraphData(commitDetails, env, metric, process);
        const dataset = getDatasetFromComponentTimeSeries(details);
        (dataset as any).label = commitDetails.commitId + ' Usage';
        datasets.push(dataset);
    } else if (type === COMPONENTS) {
        const functionsDetails = commitDetails.functions;
        Object.values(functionsDetails).forEach((fd: any) => {
            const filteredDetails = getComponentGraphData(fd, env, metric, process);
            const dataset = getDatasetFromComponentTimeSeries(filteredDetails, fd.id);
            (dataset as any).label = fd.methodName;
            datasets.push(dataset);
        });
    }
    return datasets;
}

// ===== Chart Options =====

declare global {
    var g_tooltipSelect: ((date: string | null) => void) | null;
}

export function getBasicChartOptions(metric: number, tooltipSelect: (date: string | null) => void): any {
    window.g_tooltipSelect = tooltipSelect;
    const options: any = {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {},
        scales: {
            x: {
                type: 'time',
                adapters: {
                    date: {
                        locale: enUS,
                    },
                },
                time: {
                    unit: 'day',
                    displayFormats: {
                        day: 'MM-dd',
                    },
                },
                title: {
                    display: true,
                    text: 'Time',
                },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: MetricNames[metric],
                },
            },
        },
    };
    options.events = ['mousemove', 'click', 'touchstart', 'touchmove'];
    options.plugins.tooltip = {
        enabled: false,
        mode: 'nearest',
        intersect: false,
        axis: 'xy',
        backgroundColor: '#227799',
        events: ['mousemove', 'click', 'touchstart', 'touchmove'],
        external: externalTooltipHandler
    };
    return options;
}

// ===== Custom Tooltip (Chart.js external handler) =====

const getOrCreateTooltip = (chart: any): HTMLDivElement => {
    let tooltipEl = document.getElementById('tooltipparent')?.querySelector('div') as HTMLDivElement | null;

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.borderRadius = '3px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = '1';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transition = 'all .1s ease';

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        document.getElementById('tooltipparent')?.appendChild(tooltipEl);
    }

    return tooltipEl;
};

function deleteTooltip() {
    const parent = document.getElementById('tooltipparent');
    if (parent != null && parent.hasChildNodes()) {
        parent.removeChild(parent.firstChild!);
    }
}

function externalTooltipHandler(context: any) {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = '0';
        return;
    }

    if (tooltip.body) {
        const titleLines: string[] = tooltip.title || [];
        const bodyLines: string[][] = tooltip.body.map((b: any) => b.lines);

        const tableHead = document.createElement('thead');

        titleLines.forEach(title => {
            const tr = document.createElement('tr');
            tr.style.borderWidth = '0';

            const th = document.createElement('th');
            th.style.borderWidth = '0';
            const text = document.createTextNode(title);

            th.appendChild(text);
            tr.appendChild(th);
            tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
            const colors = tooltip.labelColors[i];

            const span = document.createElement('span');
            span.style.background = colors.backgroundColor;
            span.style.borderColor = colors.borderColor;
            span.style.borderWidth = '2px';
            span.style.marginRight = '10px';
            span.style.height = '10px';
            span.style.width = '10px';
            span.style.display = 'inline-block';

            const tr = document.createElement('tr');
            tr.style.backgroundColor = 'inherit';
            tr.style.borderWidth = '0';

            const td = document.createElement('td');
            td.style.borderWidth = '0';

            const datapoint = tooltip.dataPoints[i];
            const date = datapoint.dataset.data[datapoint.dataIndex].originalDate;
            td.innerHTML = '<a href="#" onclick="g_tooltipSelect(this.dataset.date);return false;" class="profilerLink" data-date="' + date + '" title="View flame graph"><img src="' + ProfilerIcon + '" height="16" width="16"/></a>'
                + '<span> ' + body + '</span>';
            tr.appendChild(td);
            tableBody.appendChild(tr);
        });

        const tableRoot = tooltipEl.querySelector('table')!;

        while (tableRoot.firstChild) {
            tableRoot.firstChild.remove();
        }

        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
    }

    const canvasPosition = chart.canvas.getBoundingClientRect();
    const tooltipParent = document.getElementById('tooltipparent');
    const parentPosition = tooltipParent ? tooltipParent.getBoundingClientRect() : { left: 0, top: 0 };

    tooltipEl.style.opacity = '1';
    tooltipEl.style.left = (canvasPosition.left - parentPosition.left) + tooltip.caretX + 'px';
    tooltipEl.style.top = (canvasPosition.top - parentPosition.top) + tooltip.caretY + 'px';
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
}
